import { useSyncExternalStore } from 'react';

/* tutor-1 (C): EL ESTADO DEL TUTOR EN LA SESION, calcado de `creditosDeLaSesion.js`.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   `{listo, on, plan, usados, tope, temaFlojo}`. El chasis (App.js) llama `cargarTutorSiFalta(user)` al tener usuario:
   UNA llamada a `GET /tutor/estado?tz=` por sesion. La pagina del Tutor llama `refrescarTutor(user)` despues de cada
   respuesta (y al volver a la pestana y a medianoche). Al cerrar sesion, `olvidarTutor()`.

   PARA QUE LA BARRA NO PARPADEE: el ultimo `on` conocido de cada cuenta se recuerda en el aparato
   (`sb_tutor_on:<user_id>`, siempre en `try`) y se usa como valor inicial hasta que llega la llamada
   (`tutorVisible`).

   camara-pills (seccion 7, pedido de Carlos del 25-oct): EL SERVIDOR LO DICE JUNTO CON EL USUARIO. `/auth/me`, el login,
   el invitado, el alta y los inicios con Google y Apple traen `tutor_on` (la MISMA regla que `/tutor/estado`). Una
   cuenta nueva no tenia nada recordado y la barra salia con cuatro y luego entraba Sabi; ahora `tutorVisible` usa, en
   este orden, el `on` de la llamada si ya llego, el `tutor_on` del usuario si es booleano, y lo recordado. Al cargar el
   estado de una cuenta con `tutor_on` booleano, se recuerda para el siguiente arranque.

   EL CLIENTE SE PIDE AL LLAMAR, NO ARRIBA: lo consumen el menu lateral y la barra de abajo, y un `import` de `./api`
   arriba arrastra `src/i18n.js` a toda suite que monte el chasis sin doblar `lib/api` (la mina de la tanda 28b). */

function cliente() { return require('./api').default; }

const VACIO = Object.freeze({ listo: false, on: false, plan: null, usados: 0, tope: null, temaFlojo: null });

let estado = VACIO;
let dueno;
let enVuelo = null;
let generacion = 0;
const oyentes = new Set();

function avisar() {
  oyentes.forEach((fn) => fn());
}

function suscribir(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function leer() {
  return estado;
}

export const LLAVE_RECORDADA = 'sb_tutor_on:';

/** La zona horaria del aparato (nombre IANA), o '' si el navegador no la sabe. */
export function zonaDelAparato() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}

/** El ultimo `on` que el aparato vio para esa cuenta. */
export function onRecordado(idDelUsuario) {
  if (!idDelUsuario) return false;
  try {
    return window.localStorage.getItem(LLAVE_RECORDADA + idDelUsuario) === '1';
  } catch (e) {
    return false;
  }
}

function recordar(idDelUsuario, on) {
  if (!idDelUsuario) return;
  try {
    window.localStorage.setItem(LLAVE_RECORDADA + idDelUsuario, on ? '1' : '0');
  } catch (e) {
    /* sin almacenamiento: la barra espera a la llamada */
  }
}

/** Lo que ve la pagina, el menu y la barra. */
export function useTutorDeLaSesion() {
  return useSyncExternalStore(suscribir, leer, leer);
}

/** Lee el estado sin suscribirse (el gesto de la app, pruebas). */
export function tutorActual() {
  return estado;
}

/** Si el renglon "Tutor" se pinta: el `on` de la llamada si ya llego; si no, el `tutor_on` del usuario (si es
 *  booleano); si no, el recordado. */
export function tutorVisible(e, user) {
  if (!user) return false;
  if (e && e.listo) return Boolean(e.on);
  if (typeof user.tutor_on === 'boolean') return user.tutor_on;
  return onRecordado(user.id);
}

/** tutor-2 (C): la cuenta duena del estado (`{id}`), para las pantallas de estudio que no leen `useAuth` (Adaptive
 *  no lo lee y sus pruebas no lo doblan): `tutorVisible(tutor, usuarioDeLaSesion())`. Sin dueno, `null`. */
export function usuarioDeLaSesion() {
  return dueno ? { id: dueno } : null;
}

/** El dueno de la sesion cambio: lo de antes se olvida. */
function asegurarDueno(idDelUsuario) {
  const id = idDelUsuario ?? null;
  if (dueno !== undefined && dueno !== id) olvidarTutor();
  dueno = id;
}

/** Publica lo que contesto `/tutor/estado`. */
export function publicarTutor(datos, idDelUsuario) {
  const d = datos || {};
  estado = {
    listo: true,
    on: Boolean(d.on),
    plan: d.plan || null,
    usados: Number.isFinite(d.usados) ? d.usados : 0,
    tope: Number.isFinite(d.tope) ? d.tope : null,
    temaFlojo: d.tema_flojo || null,
  };
  recordar(idDelUsuario, estado.on);
  avisar();
}

/** Vuelve a preguntar. Un fallo deja lo que habia. */
export function refrescarTutor(user) {
  if (!user) return Promise.resolve(null);
  asegurarDueno(user.id);
  const miGeneracion = generacion;
  let promesa;
  try {
    promesa = cliente().get('/tutor/estado', { params: { tz: zonaDelAparato() } });
  } catch (e) {
    return Promise.resolve(null);
  }
  enVuelo = Promise.resolve(promesa)
    .then((res) => {
      if (miGeneracion !== generacion) return null;
      publicarTutor(res && res.data, user.id);
      return estado;
    })
    .catch(() => null)
    .finally(() => {
      if (miGeneracion === generacion) enVuelo = null;
    });
  return enVuelo;
}

/** UNA llamada por sesion: con dato o con una en vuelo no pide nada. */
export function cargarTutorSiFalta(user) {
  if (!user) return Promise.resolve(null);
  asegurarDueno(user.id);
  if (typeof user.tutor_on === 'boolean') recordar(user.id, user.tutor_on);
  if (estado.listo) return Promise.resolve(estado);
  if (enVuelo) return enVuelo;
  return refrescarTutor(user);
}

/** Al cerrar sesion o cambiar de usuario. */
export function olvidarTutor() {
  generacion += 1;
  enVuelo = null;
  dueno = undefined;
  if (estado === VACIO) return;
  estado = VACIO;
  avisar();
}

/** Solo pruebas. */
export function __reiniciarTutorParaPruebas() {
  generacion += 1;
  enVuelo = null;
  dueno = undefined;
  estado = VACIO;
}
